import { useEffect, useMemo, useState } from 'react'
import ReactECharts from 'echarts-for-react'
import { getPatSummary } from '../../../../services/analytics/analytics.service'
import Loading from '../../../Loading/Loading'
import DataAsOf from './DataAsOf'

const C_BAR = '#2b5784'
const LINE_COLORS = ['#2b5784', '#2a9d8f', '#f5a040', '#7b4ea0', '#c0392b', '#3b7bbf', '#1a6b55']

const SCALES = [
  { value: 'pat-maths-scale', label: 'PAT Maths' },
  { value: 'pat-reading-scale', label: 'PAT Reading' }
]

const ylNum = (y) => {
  const n = parseInt(String(y).replace(/[^\d]/g, ''), 10)
  return Number.isNaN(n) ? 999 : n
}

const Toggle = ({ value, onChange }) => (
  <div style={{ display: 'flex', gap: 6 }}>
    {SCALES.map((s) => (
      <button
        key={s.value}
        onClick={() => onChange(s.value)}
        style={{
          fontSize: 12,
          fontWeight: 600,
          padding: '5px 11px',
          borderRadius: 7,
          cursor: 'pointer',
          border: '1px solid ' + (value === s.value ? C_BAR : '#d5dbe4'),
          background: value === s.value ? C_BAR : '#fff',
          color: value === s.value ? '#fff' : '#5a6472'
        }}
      >
        {s.label}
      </button>
    ))}
  </div>
)

// Agrega summary → serie por year level, x = año, y = avg scale score ponderado por alumnos.
const buildOption = (rows, scale) => {
  const filtered = rows.filter((r) => r.scale_slug === scale && r.sitting_year != null)

  const years = [...new Set(filtered.map((r) => r.sitting_year))].sort((a, b) => a - b)
  const levels = [...new Set(filtered.map((r) => r.year_level).filter(Boolean))].sort(
    (a, b) => ylNum(a) - ylNum(b)
  )

  // acc[level][year] = { sum, n } (ponderado por students)
  const acc = {}
  filtered.forEach((r) => {
    const lvl = r.year_level || '—'
    const wy = r.sitting_year
    const students = Number(r.students || 0)
    const avg = Number(r.avg_scale_score || 0)
    if (!students || !avg) return
    acc[lvl] = acc[lvl] || {}
    acc[lvl][wy] = acc[lvl][wy] || { sum: 0, n: 0 }
    acc[lvl][wy].sum += avg * students
    acc[lvl][wy].n += students
  })

  const series = levels.map((lvl, i) => ({
    name: 'Year ' + lvl,
    type: 'line',
    smooth: true,
    connectNulls: true,
    symbol: 'circle',
    symbolSize: 7,
    lineStyle: { width: 2.4 },
    itemStyle: { color: LINE_COLORS[i % LINE_COLORS.length] },
    data: years.map((y) => {
      const cell = acc[lvl] && acc[lvl][y]
      return cell && cell.n ? +(cell.sum / cell.n).toFixed(1) : null
    })
  }))

  return {
    backgroundColor: 'transparent',
    // Leyenda VERTICAL a la derecha: cada "Year N" en su propia línea → no se pueden
    // encimar sin importar cuántos year levels haya (el layout horizontal en el fondo
    // sí se encimaba al envolver). `scroll` = respaldo si la lista excede el alto.
    grid: { left: 46, right: 104, top: 16, bottom: 28 },
    legend: {
      type: 'scroll',
      orient: 'vertical',
      right: 4,
      top: 'middle',
      textStyle: { fontSize: 11, color: '#666' },
      itemHeight: 8,
      itemWidth: 16,
      itemGap: 12
    },
    xAxis: {
      type: 'category',
      data: years,
      boundaryGap: false,
      axisLabel: { fontSize: 11, color: '#666' },
      axisTick: { show: false },
      axisLine: { lineStyle: { color: '#e0e0e0' } }
    },
    yAxis: {
      type: 'value',
      scale: true,
      name: 'Scale score',
      nameTextStyle: { fontSize: 10, color: '#aaa', align: 'left' },
      axisLabel: { fontSize: 10, color: '#aaa' },
      splitLine: { lineStyle: { color: '#f0f0f0', type: 'dashed' } }
    },
    series,
    tooltip: { trigger: 'axis' }
  }
}

const PatScoreTrend = () => {
  const [scale, setScale] = useState('pat-maths-scale')
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    getPatSummary()
      .then((res) => setData(res?.data || []))
      .catch((err) => setError(err?.message || 'Error loading PAT summary'))
      .finally(() => setLoading(false))
  }, [])

  const option = useMemo(() => buildOption(data, scale), [data, scale])
  const hasScale = useMemo(
    () => data.some((r) => r.scale_slug === scale && r.sitting_year != null),
    [data, scale]
  )

  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 16,
          flexWrap: 'wrap'
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: C_BAR }}>
            Scale score trend by year level
          </h2>
          <DataAsOf />
        </div>
        <Toggle value={scale} onChange={setScale} />
      </div>

      {loading ? (
        <Loading />
      ) : error ? (
        <div style={{ padding: 24, color: '#c0392b' }}>
          <strong>Error:</strong> {error}
        </div>
      ) : !hasScale ? (
        <p style={{ color: '#888', padding: 24 }}>
          No dated {SCALES.find((s) => s.value === scale)?.label} results yet.
        </p>
      ) : (
        <ReactECharts
          option={option}
          style={{ height: 380, width: '100%' }}
          opts={{ renderer: 'canvas' }}
          notMerge
        />
      )}
    </div>
  )
}

export default PatScoreTrend
